import React from "react";
import { Button, Col, Input, Radio, Row, Typography } from "antd";
import { COLOR_PRESETS } from "../../core/constants";

const { Text } = Typography;

const GRADIENT_ANGLES = [
  { label: "0°", value: 0 },
  { label: "45°", value: 45 },
  { label: "90°", value: 90 },
  { label: "135°", value: 135 },
  { label: "180°", value: 180 },
  { label: "225°", value: 225 },
  { label: "270°", value: 270 },
  { label: "315°", value: 315 },
];

function ColorPresetPicker({
  backgroundMode = "PRESET",
  colorPresetCode = "green_mint",
  backgroundColor = "#00a86b",
  gradientFromColor = "#ff6a00",
  gradientToColor = "#ee0979",
  gradientAngle = 90,
  textColor = "#ffffff",
  iconColor = "#ffffff",
  buttonColor = "#ffffff",
  buttonTextColor = "#ee0979",
  onModeChange,
  onPresetChange,
  onCustomColorChange,
  onGradientChange,
  onElementsColorChange,
  onResetDefaultColors,
  disabled = false,
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Mode selection tabs */}
      <Radio.Group
        value={backgroundMode}
        onChange={(e) => onModeChange && onModeChange(e.target.value)}
        disabled={disabled}
        buttonStyle="solid"
      >
        <Radio.Button value="PRESET">Bộ màu có sẵn</Radio.Button>
        <Radio.Button value="SOLID">Tùy chỉnh màu trơn</Radio.Button>
        <Radio.Button value="GRADIENT">Tùy chỉnh gradient</Radio.Button>
      </Radio.Group>

      {/* Preset Swatches */}
      {backgroundMode === "PRESET" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {COLOR_PRESETS.map((p) => {
            const isSelected = colorPresetCode === p.code;
            return (
              <div
                key={p.code}
                onClick={() => !disabled && onPresetChange && onPresetChange(p)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: isSelected ? "2px solid #2246dc" : "1px solid #e0e0e0",
                  backgroundColor: isSelected ? "#f0f4fe" : "#ffffff",
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "4px",
                    backgroundColor: p.background,
                    border: "1px solid rgba(0,0,0,0.1)",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontSize: "13px", fontWeight: isSelected ? 600 : 400 }}>{p.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Single Color Picker */}
      {backgroundMode === "SOLID" && (
        <Row gutter={12} align="middle">
          <Col span={10}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Màu nền (HEX)
            </Text>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="color"
                value={backgroundColor || "#00a86b"}
                onChange={(e) => onCustomColorChange && onCustomColorChange(e.target.value)}
                disabled={disabled}
                style={{
                  width: "40px",
                  height: "36px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  cursor: disabled ? "not-allowed" : "pointer",
                  padding: 2,
                }}
              />
              <Input
                value={backgroundColor}
                onChange={(e) => onCustomColorChange && onCustomColorChange(e.target.value)}
                disabled={disabled}
                placeholder="#00A86B"
                style={{ width: "120px" }}
              />
            </div>
          </Col>
        </Row>
      )}

      {/* Gradient Picker */}
      {backgroundMode === "GRADIENT" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Row gutter={16}>
            <Col span={8}>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Màu 1
              </Text>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="color"
                  value={gradientFromColor || "#ff6a00"}
                  onChange={(e) =>
                    onGradientChange &&
                    onGradientChange({ gradientFromColor: e.target.value, gradientToColor, gradientAngle })
                  }
                  disabled={disabled}
                  style={{
                    width: "36px",
                    height: "32px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    cursor: "pointer",
                    padding: 2,
                  }}
                />
                <Input
                  value={gradientFromColor}
                  onChange={(e) =>
                    onGradientChange &&
                    onGradientChange({ gradientFromColor: e.target.value, gradientToColor, gradientAngle })
                  }
                  disabled={disabled}
                  placeholder="#FF6A00"
                />
              </div>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Màu 2
              </Text>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="color"
                  value={gradientToColor || "#ee0979"}
                  onChange={(e) =>
                    onGradientChange &&
                    onGradientChange({ gradientFromColor, gradientToColor: e.target.value, gradientAngle })
                  }
                  disabled={disabled}
                  style={{
                    width: "36px",
                    height: "32px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    cursor: "pointer",
                    padding: 2,
                  }}
                />
                <Input
                  value={gradientToColor}
                  onChange={(e) =>
                    onGradientChange &&
                    onGradientChange({ gradientFromColor, gradientToColor: e.target.value, gradientAngle })
                  }
                  disabled={disabled}
                  placeholder="#EE0979"
                />
              </div>
            </Col>
          </Row>

          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Hướng chuyển
            </Text>
            <Radio.Group
              value={gradientAngle}
              onChange={(e) =>
                onGradientChange &&
                onGradientChange({ gradientFromColor, gradientToColor, gradientAngle: e.target.value })
              }
              disabled={disabled}
              size="small"
            >
              {GRADIENT_ANGLES.map((a) => (
                <Radio.Button key={a.value} value={a.value}>
                  {a.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>

          <div
            style={{
              height: "28px",
              borderRadius: "6px",
              background: `linear-gradient(${gradientAngle}deg, ${gradientFromColor || "#ff6a00"}, ${
                gradientToColor || "#ee0979"
              })`,
              border: "1px solid rgba(0,0,0,0.1)",
              maxWidth: "400px",
            }}
          />
        </div>
      )}

      {/* Sub elements color */}
      <div
        style={{
          marginTop: "12px",
          padding: "16px",
          backgroundColor: "#f9fafb",
          borderRadius: "8px",
          border: "1px solid #f0f0f0",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
          <span style={{ fontWeight: 600, fontSize: "13px" }}>Thành phần khác</span>
          {onResetDefaultColors && (
            <Button type="link" size="small" onClick={onResetDefaultColors} disabled={disabled} style={{ padding: 0 }}>
              Khôi phục mặc định
            </Button>
          )}
        </div>

        <Row gutter={[12, 12]}>
          <Col span={6}>
            <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
              Màu chữ nội dung
            </Text>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: 4 }}>
              <input
                type="color"
                value={textColor || "#ffffff"}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ textColor: e.target.value })}
                disabled={disabled}
                style={{ width: "32px", height: "30px", padding: 1 }}
              />
              <Input
                size="small"
                value={textColor}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ textColor: e.target.value })}
                disabled={disabled}
              />
            </div>
          </Col>

          <Col span={6}>
            <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
              Màu icon
            </Text>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: 4 }}>
              <input
                type="color"
                value={iconColor || "#ffffff"}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ iconColor: e.target.value })}
                disabled={disabled}
                style={{ width: "32px", height: "30px", padding: 1 }}
              />
              <Input
                size="small"
                value={iconColor}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ iconColor: e.target.value })}
                disabled={disabled}
              />
            </div>
          </Col>

          <Col span={6}>
            <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
              Màu nền nút CTA
            </Text>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: 4 }}>
              <input
                type="color"
                value={buttonColor || "#ffffff"}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ buttonColor: e.target.value })}
                disabled={disabled}
                style={{ width: "32px", height: "30px", padding: 1 }}
              />
              <Input
                size="small"
                value={buttonColor}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ buttonColor: e.target.value })}
                disabled={disabled}
              />
            </div>
          </Col>

          <Col span={6}>
            <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
              Màu chữ trong nút
            </Text>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: 4 }}>
              <input
                type="color"
                value={buttonTextColor || "#ee0979"}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ buttonTextColor: e.target.value })}
                disabled={disabled}
                style={{ width: "32px", height: "30px", padding: 1 }}
              />
              <Input
                size="small"
                value={buttonTextColor}
                onChange={(e) => onElementsColorChange && onElementsColorChange({ buttonTextColor: e.target.value })}
                disabled={disabled}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default React.memo(ColorPresetPicker);
